import { FormEvent, useContext, useState } from 'react';
import {
  defaultComplianceInfo,
  IComplianceDocuments,
  IComplianceInfo,
  IComplianceUpdateDTO,
} from '../../../interface/ComplianceInterface';
import useSWR from 'swr';
import { endpoints } from '../../../utils/URL';
import fetcher from '../../../utils/axiosFetcher';
import Loader from '../../common/Loader';
import { AppContext } from '../../../context/AppContext';
import { IAppState } from '../../../interface/AppInterface';
import { postComplianceAsync, uploadFileAsync } from '../../../api/compliance_api';
import { isFormValidated } from '../../../utils/formUtils';
import ButtonLoader from '../../common/ButtonLoader';
import { ConfirmationDialog } from '../../common/ConfirmationDialog';
import { useHistory } from 'react-router-dom';

const Compliance = () => {
  const { appState }: { appState: IAppState } = useContext(AppContext);
  const [progressState, setProgressState] = useState('progress-bar');
  const [complianceInfo, setComplianceInfo] = useState<IComplianceUpdateDTO>(defaultComplianceInfo);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const history = useHistory();

  const url = `${endpoints.compliance.mainUrl}`;
  const { data, isLoading } = useSWR<IComplianceInfo>(url, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    onError: (error: any) => {
      console.error('Error fetching compliance data:', error);
    },
    onSuccess: (data: IComplianceInfo) => {
      setComplianceInfo((prevState) => ({
        ...prevState,
        complianceDocuments: {
          ...prevState.complianceDocuments,
          ...data.complianceDocuments,
        },
        cacNumber: data.cacNumber,
        pharmacyCode: appState.pharmacyCode,
        pharmacyId: appState.pharmacyId,
      }));
    },
  });

  type inputNames = keyof IComplianceDocuments;
  const handleFileUpload = (input: inputNames) => async (event: any) => {
    const success = async (data: string) => {
      setProgressState('progress-bar bg-success');
      setComplianceInfo((prevState) => ({
        ...prevState,
        complianceDocuments: {
          ...prevState.complianceDocuments,
          [input]: data,
        },
      }));
    };
    const error = () => {
      setProgressState('progress-bar bg-danger');
      setProgressState('progress-bar');
    };

    const progressBarId = `${input}-progress-bar`;
    await uploadFileAsync(input, progressBarId, appState.pharmacyCode, success, error);
  };

  const handleSubmit = async (event: FormEvent) => {
    if (!isFormValidated('compliance-form')) {
      return;
    }
    event.preventDefault();
    setLoading(true);

    await postComplianceAsync(complianceInfo)
      .then((response: any) => {
        if (response.success) {
          setModalOpen(!modalOpen);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const closeModal = () => {
    setModalOpen(!modalOpen);
    history.push('/entry');
  };

  return (
    <section className='col-md-10 content-section mb-5'>
      <h5 className=' text-center mt-3'>Compliance Documents</h5>
      <p className='text-center text-secondary'>
        Please upload your compliance documents to enable you to access the full features of
        Wellapartner.
      </p>
      <div className='mt-3'>{isLoading && <Loader />}</div>
      <div className='row justify-content-center'>
        <div className='col-lg-10'>
          {!isLoading && data && (
            <form id='compliance-form' onSubmit={handleSubmit}>
              <fieldset className='row' disabled={loading}>
                <div className='col-lg-4 col-md-6  mt-3'>
                  <div className='card prescription-card px-4 py-2 h-100'>
                    <div className='card-body text-center'>
                      <span className='bi bi-file-earmark-text fs-60'></span>
                      <div className='card-title fs-18'>PCN Premise License</div>

                      {data.complianceDocuments?.psnCertificate ? (
                        <span className='fs-36 bi bi-check-circle-fill text-success mx-2'></span>
                      ) : (
                        <div>
                          <small className='d-block fs-14 text-danger'>
                            Not earlier than {new Date().getFullYear() - 1}
                          </small>
                          <input
                            id='psnCertificate'
                            type='file'
                            className='form-control form-control-sm mt-3'
                            onChange={handleFileUpload('psnCertificate')}
                            required
                          />
                          <div className={'progress'} style={{ height: '3px', marginTop: '10px' }}>
                            <div
                              id='psnCertificate-progress-bar'
                              className={progressState}
                              role='progressbar'
                              style={{ width: '0%' }}
                              aria-valuenow={0}
                              aria-valuemin={0}
                              aria-valuemax={100}
                            />
                          </div>
                          {complianceInfo.complianceDocuments.psnCertificate && (
                            <a
                              className='text-truncate mt-3 text-primary fs-14 d-block btn'
                              href={complianceInfo.complianceDocuments.psnCertificate}
                            >
                              View File
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className='col-lg-4 col-md-6 mt-3'>
                  <div className='card prescription-card px-4 py-2 h-100'>
                    <div className='card-body text-center'>
                      <span className='bi bi-file-earmark-text fs-60'></span>
                      <div className='card-title fs-18'>CAC Certificate</div>
                      {data.complianceDocuments?.cacCertificate ? (
                        <span className='fs-36 bi bi-check-circle-fill text-success mx-2'></span>
                      ) : (
                        <div>
                          <input
                            id='cacCertificate'
                            type='file'
                            className='form-control form-control-sm mt-3'
                            onChange={handleFileUpload('cacCertificate')}
                          />
                          <div className={'progress'} style={{ height: '3px', marginTop: '10px' }}>
                            <div
                              id='cacCertificate-progress-bar'
                              className={progressState}
                              role='progressbar'
                              style={{ width: '0%' }}
                              aria-valuenow={0}
                              aria-valuemin={0}
                              aria-valuemax={100}
                            />
                          </div>
                          {complianceInfo.complianceDocuments.cacCertificate && (
                            <a
                              className='text-truncate mt-3 text-primary fs-14 d-block btn'
                              href={complianceInfo.complianceDocuments.cacCertificate}
                            >
                              View File
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className='col-lg-4 col-md-6  mt-3'>
                  <div className='card prescription-card px-4 py-2 h-100 '>
                    <div className='card-body text-center'>
                      <span className='bi bi-hash fs-60'></span>
                      <div className='card-title fs-18'>CAC Number</div>
                      {data.cacNumber ? (
                        <span className='fs-36 bi bi-check-circle-fill text-success mx-2'></span>
                      ) : (
                        <input
                          type='text'
                          className='form-control form-control-sm mt-3'
                          placeholder='Enter your CAC No.'
                          required
                          value={complianceInfo.cacNumber}
                          onChange={(e) =>
                            setComplianceInfo((prevState) => ({
                              ...prevState,
                              cacNumber: e.target.value,
                            }))
                          }
                        />
                      )}
                      {complianceInfo.complianceDocuments.cacCertificate && (
                        <div
                          className='font-bold text-truncate mt-3 text-primary fs-16'
                          title={complianceInfo.cacNumber}
                        >
                          RC/{complianceInfo.cacNumber}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {(!data.complianceDocuments?.cacCertificate ||
                  !data.complianceDocuments?.psnCertificate ||
                  !data.cacNumber) && (
                  <div className='mt-5 text-center'>
                    <button
                      type='submit'
                      className='btn btn-primary btn-lg col-md-4'
                      onSubmit={handleSubmit}
                    >
                      Submit
                      {loading && <ButtonLoader />}
                    </button>
                  </div>
                )}
              </fieldset>
            </form>
          )}
        </div>
      </div>

      <ConfirmationDialog
        open={modalOpen}
        close={closeModal}
        message='Your compliance documents have been successfully submitted. It will be reviewed by our team and you will be notified once it is approved.'
        title='Success'
        iconClass='bi bi-check-circle-fill text-success'
        showButtons={false}
        keepOpen={false}
        onConfirm={() => {
          setModalOpen(false);
          window.location.reload();
        }}
      />
    </section>
  );
};

export default Compliance;
